import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';

/**
 * /data-hub/elections?place=<tag slug>&election=<id>  (both read from the address; data loaded once, see BUNDLE)
 * Upcoming elections for a place (Nigeria, a state, or a place abroad) with a countdown, the chosen
 * election's main poll (a forum poll, voted on right here) and the forum's political polls for that place.
 */
const go = (e, url) => {
  if (e.ctrlKey || e.metaKey) return;
  e.preventDefault();
  m.route.set(url);
};
const link = (url, children, cls) => m("a", {
  className: cls,
  href: url,
  onclick: e => go(e, url)
}, children);
const day = iso => new Date(iso + 'T00:00:00');
const dateLabel = iso => iso ? day(iso).toLocaleDateString('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'long',
  year: 'numeric'
}) : '';
const shortDate = iso => iso ? new Date(iso).toLocaleDateString('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric'
}) : '';

/** Whole days from today to the date (local time) */
function daysTo(iso) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((day(iso) - today) / 864e5);
}
function countdown(iso) {
  const n = daysTo(iso);
  if (n < 0) return 'Held ' + shortDate(iso);
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  return 'In ' + n.toLocaleString() + ' days';
}
const STATUS = {
  preliminary: 'Preliminary',
  unofficial: 'Unofficial',
  certified: 'Certified'
};
const STATUS_HELP = {
  preliminary: 'Early figures: partial counts or projections, still changing.',
  unofficial: 'Full count, not yet certified by the election authority.',
  certified: 'Final result, declared or certified by the election authority.'
};
const winner = e => {
  const w = e.results && (e.results.candidates || []).find(c => c.winner);
  return w ? w.name : null;
};
const host = u => String(u).replace(/^https?:\/\/(www\.)?/, '').split('/')[0];

/**
 * Everything the page shows comes in one request (every place's elections and polls) and is kept for the
 * visit, so switching place or election is instant: only the address changes. Refreshed after 5 minutes.
 */
let BUNDLE = null; // {data, at, asked: {slug: true}}
const FRESH_MS = 5 * 60 * 1000;
const pollCache = {}; // discussion id -> discussion (with its poll), for the visit
let leftAt = 0; // when this page was last removed

export default class ElectionsPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.bodyClass = 'DataHubFull';
    // switching place or election re-creates the page: keep the scroll position then, but start at the top
    // when arriving from elsewhere
    this.scrollTopOnCreate = Date.now() - leftAt > 1500;
    this.failed = false;
    app.setTitle('Elections');
    this.ensure();
  }
  onremove(vnode) {
    super.onremove(vnode);
    leftAt = Date.now();
  }

  /** Fetch the bundle unless it's fresh and has the place in the address */
  ensure() {
    const place = m.route.param('place') || 'nigeria';
    const fresh = BUNDLE && Date.now() - BUNDLE.at < FRESH_MS;
    if (fresh && (BUNDLE.data.views[place] || BUNDLE.asked[place])) return;
    if (this.loading) return;
    this.loading = true;
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/datahub/elections?place=' + encodeURIComponent(place),
      errorHandler: () => {}
    }).then(r => {
      BUNDLE = {
        data: r,
        at: Date.now(),
        asked: Object.assign({}, fresh ? BUNDLE.asked : {}, {
          [place]: true
        })
      };
    }).catch(() => {
      if (!BUNDLE) this.failed = true;
    }).finally(() => {
      this.loading = false;
      m.redraw();
    });
  }

  /** What to show, from the bundle and the address */
  current() {
    if (!BUNDLE) return null;
    const b = BUNDLE.data;
    const asked = m.route.param('place') || 'nigeria';
    const place = b.views[asked] ? asked : 'nigeria';
    const v = b.views[place] || {
      name: 'Nigeria',
      upcoming: [],
      recent: [],
      first: null,
      polls: []
    };
    const row = id => b.elections[String(id)];
    const wanted = Number(m.route.param('election') || 0);
    const ids = v.upcoming.concat(v.recent);
    const selected = row(ids.indexOf(wanted) !== -1 ? wanted : v.first) || null;
    const places = b.places.slice();
    if (!places.some(p => p.key === place)) places.push({
      key: place,
      label: v.name,
      count: v.upcoming.length
    });
    return {
      place,
      placeName: v.name,
      places,
      upcoming: v.upcoming.map(row).filter(Boolean),
      recent: v.recent.map(row).filter(Boolean),
      selected,
      // the main poll isn't repeated in the list
      polls: v.polls.filter(p => !selected || p.discussionId !== selected.pollDiscussionId)
    };
  }

  /** The main poll: its discussion (with the poll, its options and my votes), rendered with FoF Polls' own card */
  pollFor(id) {
    if (!id) return null;
    if (!pollCache[id]) {
      pollCache[id] = {
        loading: true,
        discussion: null
      };
      app.store.find('discussions', String(id)).then(d => {
        pollCache[id].discussion = d;
      }).catch(() => {}).finally(() => {
        pollCache[id].loading = false;
        m.redraw();
      });
    }
    return pollCache[id];
  }
  url(place, election) {
    const q = [];
    if (place && place !== 'nigeria') q.push('place=' + encodeURIComponent(place));
    if (election) q.push('election=' + election);
    return '/data-hub/elections' + (q.length ? '?' + q.join('&') : '');
  }

  /** Open an election: change the address and bring its card into view */
  pick(ev, url) {
    if (ev.ctrlKey || ev.metaKey) return;
    ev.preventDefault();
    m.route.set(url);
    setTimeout(() => {
      const el = document.querySelector('.DHElections-main, .DHElections-places');
      if (el) window.scrollTo({
        top: el.getBoundingClientRect().top + window.pageYOffset - 90,
        behavior: 'smooth'
      });
    }, 30);
  }
  view() {
    this.ensure();
    const d = this.current();
    if (d) this.poll = this.pollFor(d.selected && d.selected.pollDiscussionId);
    return m("div", {
      className: "DataHubFull-page DHElections"
    }, m("section", {
      className: "DHHero DHState-hero"
    }, m("div", {
      className: "container DHHero-inner"
    }, m("div", {
      className: "DHHero-text"
    }, link('/data-hub', [m("i", {
      className: "fas fa-arrow-left"
    }), ' Data Hub'], 'DataHubPub-back'), m("div", {
      className: "DHHero-eyebrow"
    }, "Elections"), m("h1", null, d && d.place !== 'nigeria' ? 'Elections: ' + d.placeName : 'Upcoming elections'), m("p", null, "When the next elections are, in Nigeria, your state and abroad, with a Voucha poll on each race.")))), m("div", {
      className: "container DHState"
    }, d ? this.body(d) : this.failed ? m("p", {
      className: "DHMuted"
    }, "Elections couldn't load. Please refresh the page.") : m(LoadingIndicator, null)));
  }
  body(d) {
    const sel = d.selected;
    return [m("nav", {
      className: "DHElections-places",
      "aria-label": "Places"
    }, d.places.map(p => m("a", {
      className: 'DHElections-place' + (p.key === d.place ? ' is-active' : ''),
      href: this.url(p.key),
      onclick: e => go(e, this.url(p.key))
    }, p.label, p.count ? m("span", null, p.count) : null))), sel ? this.selected(sel, d) : m("section", {
      className: "DHSection"
    }, m("p", {
      className: "DHMuted"
    }, "No upcoming elections are listed for ", d.placeName, " yet.")), d.upcoming.length > 1 || d.upcoming.length && sel && daysTo(sel.date) < 0 ? m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("h2", null, "Coming up", d.place !== 'nigeria' ? ' for ' + d.placeName : '')), m("div", {
      className: "DHElections-list"
    }, d.upcoming.map(e => this.card(e, d, sel)))) : null,
    // past elections and their results, right after what's coming (hidden when there are none)
    d.recent.length ? m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("div", null, m("h2", null, "Past elections and results"), m("p", {
      className: "DHMuted"
    }, "Held in the past 6 months. Pick one to see its full results."))), m("div", {
      className: "DHElections-list"
    }, d.recent.map(e => this.card(e, d, sel)))) : null,
    // political polls (hidden when there are none)
    d.polls.length ? m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("div", null, m("h2", null, "Political polls"), m("p", {
      className: "DHMuted"
    }, d.place === 'nigeria' ? 'Polls on Nigerian politics from the community.' : 'Polls on politics in ' + d.placeName + ' from the community.')), link('/t/politics', 'Politics discussions', 'DHLink')), m("ul", {
      className: "DHState-pollList"
    }, d.polls.map(p => m("li", null, m("span", {
      className: "DHState-pollIcon",
      "aria-hidden": "true"
    }, m("i", {
      className: "fas fa-square-poll-vertical"
    })), m("span", {
      className: "DHState-pollBody"
    }, link(p.url, p.question), m("span", {
      className: "DHMuted"
    }, p.voters.toLocaleString() + (p.voters === 1 ? ' participant' : ' participants'), p.ended ? ' · Closed' : ' · ' + shortDate(p.date))), p.ended ? null : link(p.url, 'Vote', 'DHState-pollVote'))))) : null];
  }
  card(e, d, sel) {
    const n = daysTo(e.date);
    return m("a", {
      className: 'DHElections-card' + (sel && sel.id === e.id ? ' is-active' : ''),
      href: this.url(d.place, e.id),
      onclick: ev => this.pick(ev, this.url(d.place, e.id))
    }, m("span", {
      className: "DHElections-cardDate"
    }, m("strong", null, day(e.date).toLocaleDateString('en-GB', {
      day: 'numeric'
    })), m("span", null, day(e.date).toLocaleDateString('en-GB', {
      month: 'short',
      year: 'numeric'
    }))), m("span", {
      className: "DHElections-cardBody"
    }, m("strong", null, e.title), m("span", {
      className: "DHMuted"
    }, e.levelLabel, " \xB7 ", e.placeName, winner(e) ? ' · Won by ' + winner(e) + (e.resultsStatus !== 'certified' ? ' (' + STATUS[e.resultsStatus].toLowerCase() + ')' : '') : e.pollDiscussionId && n >= 0 ? ' · Poll open' : '')), m("span", {
      className: 'DHElections-when' + (n < 0 ? ' is-past' : '')
    }, countdown(e.date)));
  }
  selected(e, d) {
    const n = daysTo(e.date);
    return m("section", {
      className: "DHSection DHElections-main"
    }, m("div", {
      className: "DHPair"
    }, m("div", {
      className: "DHPanel DHElections-info"
    }, m("span", {
      className: "DHElections-badge"
    }, e.levelLabel, " \xB7 ", e.placeName), m("h2", null, e.title), m("div", {
      className: "DHElections-count"
    }, n >= 0 ? [m("strong", null, n === 0 ? 'Today' : n.toLocaleString()), n === 0 ? null : m("span", null, n === 1 ? 'day to go' : 'days to go')] : m("span", null, "Held")), m("p", {
      className: "DHElections-date"
    }, m("i", {
      className: "far fa-calendar"
    }), " ", dateLabel(e.date), e.dateNote ? m("span", {
      className: "DHMuted"
    }, " \xB7 ", e.dateNote) : null), e.about ? m("p", {
      className: "DHElections-about"
    }, e.about) : null, e.infoUrl ? m("a", {
      className: "DHLink",
      href: e.infoUrl,
      target: "_blank",
      rel: "noopener"
    }, "Official information ", m("i", {
      className: "fas fa-external-link-alt"
    })) : null), e.pollDiscussionId ? m("div", {
      className: "DHPanel DHElections-poll"
    }, this.mainPoll(e)) : null), e.results && e.results.candidates && e.results.candidates.length ? this.results(e) : null);
  }
  results(e) {
    const r = e.results;
    const max = Math.max(1, ...r.candidates.map(c => c.percent || 0));
    return m("div", {
      className: "DHPanel DHElections-results"
    }, m("div", {
      className: "DHElections-resultsHead"
    }, m("h3", null, "Results"), m("span", {
      className: 'DHElections-status is-' + e.resultsStatus,
      title: STATUS_HELP[e.resultsStatus]
    }, STATUS[e.resultsStatus])), m("p", {
      className: "DHMuted"
    }, STATUS_HELP[e.resultsStatus], r.reportingNote ? ' ' + r.reportingNote + '.' : '', e.resultsUpdatedAt ? ' Updated ' + new Date(e.resultsUpdatedAt).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }) + '.' : ''), m("table", {
      className: "DHElections-table"
    }, m("thead", null, m("tr", null, m("th", null, "Candidate"), m("th", null, "Votes"), m("th", null, "Share"))), m("tbody", null, r.candidates.map(c => m("tr", {
      className: c.winner ? 'is-winner' : ''
    }, m("td", null, m("strong", null, c.name), c.winner ? m("span", {
      className: "DHElections-won"
    }, m("i", {
      className: "fas fa-check"
    }), " ", e.resultsStatus === 'certified' ? 'Winner' : 'Leading') : null, c.party ? m("span", {
      className: "DHMuted"
    }, c.party) : null), m("td", null, c.votes !== null && c.votes !== undefined ? c.votes.toLocaleString() : '—'), m("td", {
      className: "DHElections-share"
    }, c.percent !== null && c.percent !== undefined ? [m("span", {
      className: "DHElections-bar",
      style: {
        width: c.percent / max * 100 + '%'
      }
    }), m("span", null, c.percent.toFixed(1), "%")] : '—'))))), m("p", {
      className: "DHMuted DHElections-sources"
    }, r.turnout ? 'Turnout ' + r.turnout + '%. ' : '', e.resultsUrl ? ['Source: ', m("a", {
      href: e.resultsUrl,
      target: "_blank",
      rel: "noopener"
    }, host(e.resultsUrl))] : (e.sources || []).length ? ['Sources: ', (e.sources || []).slice(0, 3).map((u, i) => [i ? ', ' : '', m("a", {
      href: u,
      target: "_blank",
      rel: "noopener"
    }, host(u))])] : null));
  }
  mainPoll(e) {
    if (!e.pollDiscussionId) {
      return [m("h3", null, "The Voucha poll"), m("p", {
        className: "DHMuted"
      }, "There's no poll for this election yet.")];
    }
    const p = this.poll;
    if (!p || p.loading) return m(LoadingIndicator, null);
    const disc = p.discussion;
    const post = disc && disc.firstPost && disc.firstPost();
    const polls = post && post.polls && post.polls() || [];
    const url = disc ? app.route.discussion(disc) : '/d/' + e.pollDiscussionId;
    let PostPoll = null;
    try {
      PostPoll = flarum.reg.checkModule('fof-polls', 'forum/components/PostPoll');
    } catch (err) {
      PostPoll = null;
    }
    const discuss = m("p", {
      className: "DHElections-discuss"
    }, link(url, [m("i", {
      className: "far fa-comments"
    }), ' ', disc ? 'Discuss it: ' + disc.title() : 'Open the poll']));
    if (!polls.length || !PostPoll) {
      return [m("h3", null, "The Voucha poll"), m("p", {
        className: "DHMuted"
      }, "Vote in the discussion."), discuss];
    }
    return [m("h3", null, "The Voucha poll"), polls.filter(Boolean).map(poll => m(PostPoll, {
      key: poll.id(),
      poll: poll,
      post: post
    })), discuss];
  }
}